/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { usePartograph } from "./hooks/usePartograph";
// import { PartographChart } from "./PartographChart";
import { PartographEntryForm } from "./PartographEntryForm";
import { PartographEntries } from "./PartographEntries";
import { history } from "../../../../Helpers/history";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import TextField from "../../../Utils/FormInputs/TextField";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import {
    PGPageWrapper,
    PGAlertContainer,
    PGAlertCard,
    PGAlertIcon,
    PGAlertContent,
    PGAlertTitle,
    PGAlertDetail,
    PGAlertAction,
    PGSummaryStrip,
    PGSummaryItem,
    PGSummaryLabel,
    PGSummaryValue,
    PGStartCard,
    PGStartIcon,
    PGStartTitle,
    PGStartDesc,
    PGStartFields,
    PGPrimaryBtn,
    PGNoAdmission,
    PGNoAdmissionIcon,
    PGNoAdmissionTitle,
    PGNoAdmissionDesc,
    PGLoading,
    PGDangerBtn,
} from "./PartographStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";

/**
 * PartographPage — Page-level container for Digital Partograph
 *
 * Route: /gyn-and-obs-partograph/:patient_number/:visit_id/:patient_type
 *
 * States:
 *  1. Loading — fetching admission data
 *  2. No Admission — patient not admitted to an OB/GYN admission
 *  3. No Partograph — admitted but partograph not started (show start card)
 *  4. Active Partograph — tabs: Chart, Record Entry, All Entries
 */
const PartographPage = function(props) {
    const { actions, snackbar, match } = props;
    const { patient_number, visit_id, patient_type } = match.params;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── Partograph hook ─── */
    const pg = usePartograph(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };
    const { openBar, type, message } = snackbar;

    /* ─── Navigate to admission module ─── */
    const goToAdmission = function() {
        history.push("/gyn-and-obs-admission/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    /* ─── Navigate to delivery record ─── */
    const goToDeliveryRecord = function() {
        history.push("/gyn-and-obs-delivery-record/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    const isCompleted = pg.partograph && pg.partograph.status === "completed";

    return (
        <div className="journals">
            <PageTitle title="Digital Partograph" />
            <SubHeader
                title="OB/GYN"
                subTitle="Digital Partograph"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="Partograph"
                    />
                </div>
            </SubHeader>

            <MainSnackbar
                variant={type}
                open={openBar}
                message={message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <ObGynPatientInfo
                    patient_number={patient_number}
                    visit_id={visit_id}
                />

                <PGPageWrapper>

                    {/* ─── Loading State ─── */}
                    {pg.loadingAdmission ? (
                        <PGLoading>Loading admission data...</PGLoading>
                    ) : !pg.admissionData ? (
                        /* ─── No Admission ─── */
                        <PGNoAdmission>
                            <PGNoAdmissionIcon>🏥</PGNoAdmissionIcon>
                            <PGNoAdmissionTitle>No Admission Found</PGNoAdmissionTitle>
                            <PGNoAdmissionDesc>
                                The mother must be admitted before a partograph can be started.
                            </PGNoAdmissionDesc>
                            <div style={{ marginTop: "16px", display: "flex", justifyContent: "center" }}>
                                <button className="btn btn-sm sms-info-btn" onClick={goToAdmission}>
                                    Admit Patient
                                </button>
                            </div>
                        </PGNoAdmission>
                    ) : !pg.partograph ? (
                        /* ─── Partograph Not Started ─── */
                        <PGStartCard>
                            <PGStartTitle>Start Digital Partograph</PGStartTitle>
                            <PGStartDesc>
                                Begin monitoring labour progress. The partograph will track cervical dilation,
                                fetal heart rate, contractions, and maternal vitals over time.
                                Start when active labour is confirmed (typically at 4cm dilation).
                            </PGStartDesc>
                            <PGStartFields>
                                <Form.Group>
                                    <Label name="Active Labour Start Time" type />
                                    <TextField
                                        type="datetime-local"
                                        name="started_at"
                                        value={pg.startForm.started_at}
                                        onChange={pg.handleStartChange}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Notes" />
                                    <ResizableTextarea
                                        rows="2"
                                        name="notes"
                                        value={pg.startForm.notes}
                                        onChange={pg.handleStartChange}
                                        placeholder="Initial assessment notes..."
                                    />
                                </Form.Group>
                            </PGStartFields>
                            <button className="btn btn-sm sms-info-btn"
                                disabled={pg.starting}
                                onClick={pg.startPartograph}
                                style={{ margin: "0 auto" }}
                            >
                                {pg.starting ? "Starting..." : "Start Partograph"}
                            </button>
                        </PGStartCard>
                    ) : (
                        /* ─── Active / Completed Partograph ─── */
                        <>
                            {/* Completed Banner */}
                            {isCompleted && (
                                <div style={{
                                    background: "#e8f5e9",
                                    border: "1px solid #a5d6a7",
                                    borderRadius: "8px",
                                    padding: "16px 20px",
                                    marginBottom: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                }}>
                                    <div>
                                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#2e7d32", marginBottom: "4px" }}>
                                            Partograph Completed
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#546e7a" }}>
                                            {pg.summary
                                                ? "Labour duration: " + pg.summary.durationHours + "h · Final dilation: " + pg.summary.latestDilation + "cm · " + pg.summary.totalEntries + " entries recorded"
                                                : "No entries were recorded."
                                            }
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm sms-info-btn"
                                        onClick={goToDeliveryRecord}
                                    >
                                        Proceed to Delivery Record
                                    </button>
                                </div>
                            )}

                            {/* Summary Strip */}
                            {pg.summary && (
                                <PGSummaryStrip>
                                    <PGSummaryItem>
                                        <PGSummaryLabel>Duration</PGSummaryLabel>
                                        <PGSummaryValue>{pg.summary.durationHours}h</PGSummaryValue>
                                    </PGSummaryItem>
                                    <PGSummaryItem>
                                        <PGSummaryLabel>Dilation</PGSummaryLabel>
                                        <PGSummaryValue color="#004d40">
                                            {pg.summary.latestDilation}cm
                                        </PGSummaryValue>
                                    </PGSummaryItem>
                                    <PGSummaryItem>
                                        <PGSummaryLabel>Descent</PGSummaryLabel>
                                        <PGSummaryValue color="#e65100">
                                            {pg.summary.latestDescent}
                                        </PGSummaryValue>
                                    </PGSummaryItem>
                                    <PGSummaryItem warn={
                                        pg.summary.latestFHR !== "—" &&
                                        (parseFloat(pg.summary.latestFHR) < 110 || parseFloat(pg.summary.latestFHR) > 160)
                                    }>
                                        <PGSummaryLabel>FHR</PGSummaryLabel>
                                        <PGSummaryValue color={
                                            pg.summary.latestFHR !== "—" &&
                                            (parseFloat(pg.summary.latestFHR) < 110 || parseFloat(pg.summary.latestFHR) > 160)
                                                ? "#c62828" : "#1565c0"
                                        }>
                                            {pg.summary.latestFHR} bpm
                                        </PGSummaryValue>
                                    </PGSummaryItem>
                                    <PGSummaryItem>
                                        <PGSummaryLabel>BP</PGSummaryLabel>
                                        <PGSummaryValue>{pg.summary.latestBP}</PGSummaryValue>
                                    </PGSummaryItem>
                                    <PGSummaryItem>
                                        <PGSummaryLabel>Entries</PGSummaryLabel>
                                        <PGSummaryValue>{pg.summary.totalEntries}</PGSummaryValue>
                                    </PGSummaryItem>
                                </PGSummaryStrip>
                            )}

                            {/* Tab Bar — hidden when completed */}
                            {!isCompleted && (
                                <ObGynTabBar>
                                    <ObGynTabBtn
                                        active={pg.activeTab === "entry"}
                                        onClick={function() { pg.setActiveTab("entry"); }}
                                    >
                                        Record Entry
                                    </ObGynTabBtn>
                                    <ObGynTabBtn
                                        active={pg.activeTab === "entries"}
                                        onClick={function() { pg.setActiveTab("entries"); }}
                                    >
                                        All Entries
                                        <ObGynTabBadge active={pg.activeTab === "entries"}>
                                            {pg.entries.length}
                                        </ObGynTabBadge>
                                    </ObGynTabBtn>
                                </ObGynTabBar>
                            )}

                            {/* Tab Content */}
                            {!isCompleted && pg.activeTab === "entry" && (
                                <PartographEntryForm pg={pg} />
                            )}

                            <PartographEntries pg={pg} isCompleted={isCompleted} />

                            {/* Complete Partograph button */}
                            {pg.activeTab === "entries" && pg.partograph.status === "active" && pg.entries.length > 0 && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: "16px",
                                    gap: "10px",
                                }}>
                                    <button className="btn sms-info-btn" onClick={pg.completePartograph}>
                                        Complete Partograph
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                </PGPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PartographPage);
